import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { gameResultAPI } from '../api';
import AntiCheatReport from '../components/AntiCheatReport';

const GRADE_OPTIONS = [
  { type: 'both', bonus: false, label: 'Oba správně', detail: '+1 / +1', color: 'bg-green-500 hover:bg-green-600', icon: '✅' },
  { type: 'solver-only', bonus: false, label: 'Jen řešitel', detail: '0 / +1', color: 'bg-blue-500 hover:bg-blue-600', icon: '👤' },
  { type: 'creator-only', bonus: false, label: 'Jen tvůrce', detail: '+1 / 0', color: 'bg-yellow-500 hover:bg-yellow-600', icon: '✨' },
  { type: 'none', bonus: false, label: 'Žádný', detail: '0 / 0', color: 'bg-gray-500 hover:bg-gray-600', icon: '❌' },
  { type: 'penalty', bonus: false, label: 'Penalizace', detail: '-1 / 0', color: 'bg-red-500 hover:bg-red-600', icon: '🚫' },
  { type: 'both', bonus: true, label: 'Oba + bonus', detail: '+1.5 / +1.5', color: 'bg-purple-500 hover:bg-purple-600', icon: '⭐' },
];

const GAME_MODE_LABELS = {
  'explain-it': 'Vysvětli to',
  'verbal-to-code': 'Ze slov do kódu',
  'fill-in-code': 'Doplň kód',
  'click-the-bug': 'Najdi chybu',
  'draw': 'Interaktivní kresba',
  'teacher-draw': 'Kresba ze zadání',
  'draw-guess': 'Kresba s hádáním',
  'abcd': 'A/B/C/D',
  'true-false': 'Pravda / Nepravda',
  'text-answer': 'Textová odpověď',
  'sort-options': 'Řazení možností',
  'fill-sentence': 'Doplnění věty',
};

const ReviewGame = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gameResult, setGameResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRound, setActiveRound] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showAntiCheat, setShowAntiCheat] = useState(false);

  useEffect(() => { loadResult(); }, [id]);

  const loadResult = async () => {
    try {
      const response = await gameResultAPI.getOne(id);
      setGameResult(response.data);
    } catch {
      alert('Nepodařilo se načíst výsledky hry');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleGradePair = async (roundIndex, pairIndex, type, bonus = false, customPoints = null) => {
    setSaving(true);
    try {
      const grading = customPoints
        ? { type: 'custom', creatorPoints: customPoints.creator, solverPoints: customPoints.solver }
        : { type, bonus };
      const response = await gameResultAPI.gradePair(id, { roundIndex, pairIndex, grading });
      setGameResult(prev => {
        const updated = { ...prev };
        updated.rounds = [...prev.rounds];
        updated.rounds[roundIndex] = { ...prev.rounds[roundIndex] };
        updated.rounds[roundIndex].pairs = [...prev.rounds[roundIndex].pairs];
        updated.rounds[roundIndex].pairs[pairIndex] = response.data.pair;
        updated.players = response.data.players;
        return updated;
      });
    } catch (err) {
      console.error('Chyba při hodnocení páru:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleGradeSolo = async (roundIndex, sessionId, points) => {
    setSaving(true);
    try {
      const response = await gameResultAPI.gradeSolo(id, { roundIndex, sessionId, points });
      setGameResult(prev => {
        const updated = { ...prev };
        updated.rounds = [...prev.rounds];
        updated.rounds[roundIndex] = { ...prev.rounds[roundIndex] };
        updated.rounds[roundIndex].soloAnswers = prev.rounds[roundIndex].soloAnswers.map(sa =>
          sa.sessionId === sessionId ? response.data.soloAnswer : sa
        );
        updated.players = response.data.players;
        return updated;
      });
    } catch (err) {
      console.error('Chyba při úpravě solo bodů:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkComplete = async () => {
    try {
      await gameResultAPI.markComplete(id);
      setGameResult(prev => ({ ...prev, gradingComplete: true }));
    } catch {
      console.error('Chyba při označení jako dokončeno');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Načítání...</div>
      </div>
    );
  }

  if (!gameResult) return null;

  const currentRound = gameResult.rounds[activeRound];
  const totalGradedPairs = gameResult.rounds.reduce((acc, r) => acc + (r.pairs || []).filter(p => p.grading?.type).length, 0);
  const totalPairs = gameResult.rounds.reduce((acc, r) => acc + (r.pairs || []).length, 0);

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">{gameResult.gamePackName}</h1>
              <p className="text-gray-500 text-sm mt-1">
                Místnost: {gameResult.roomCode} | {new Date(gameResult.finishedAt).toLocaleString('cs')} | {gameResult.players.length} hráčů
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAntiCheat(v => !v)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm border-2 transition-colors flex items-center gap-2 ${
                  showAntiCheat
                    ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                    : 'bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50'
                }`}
                title="Zobrazit/skrýt anti-cheat záznamy pro každého žáka"
              >
                🛡️ Anti-cheat {showAntiCheat ? 'zapnuto' : 'vypnuto'}
              </button>
              {!gameResult.gradingComplete && (
                <button onClick={handleMarkComplete} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold">
                  Označit jako ohodnoceno
                </button>
              )}
              <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                ← Zpět
              </button>
            </div>
          </div>
          {gameResult.gradingComplete && (
            <div className="mt-3 bg-green-100 border border-green-300 rounded-lg px-4 py-2 text-green-700 text-sm font-semibold">
              ✅ Hodnocení dokončeno
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-bold text-gray-700 mb-3">Kola</h3>
              <div className="space-y-2">
                {gameResult.rounds.map((round, i) => {
                  const pairsInRound = (round.pairs || []).length;
                  const gradedInRound = (round.pairs || []).filter(p => p.grading?.type).length;
                  return (
                    <button
                      key={i}
                      onClick={() => setActiveRound(i)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeRound === i
                          ? 'bg-primary-100 border-2 border-primary-400 text-primary-700'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <div className="font-semibold">{i + 1}. {GAME_MODE_LABELS[round.round?.type] || round.round?.type}</div>
                      {round.isSolo
                        ? <div className="text-xs text-gray-500">{round.soloAnswers?.length || 0} odpovědí</div>
                        : <div className="text-xs text-gray-500">{gradedInRound}/{pairsInRound} ohodnoceno</div>
                      }
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-bold text-gray-700 mb-3">Žebříček</h3>
              <div className="space-y-1">
                {[...gameResult.players].sort((a, b) => b.finalScore - a.finalScore).map((player, i) => (
                  <div key={player.sessionId} className="flex justify-between items-center px-2 py-1 text-sm">
                    <span className={i < 3 ? 'font-bold' : ''}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`} {player.name}
                    </span>
                    <span className="font-mono font-bold">{player.finalScore}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-xs text-gray-500 text-center">{totalGradedPairs}/{totalPairs} párů ohodnoceno</div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {currentRound && currentRound.isSolo ? (
              <SoloRoundReview round={currentRound} roundIndex={activeRound} onGradeSolo={handleGradeSolo} saving={saving} showAntiCheat={showAntiCheat} />
            ) : currentRound ? (
              <InteractiveRoundReview round={currentRound} roundIndex={activeRound} onGrade={handleGradePair} saving={saving} showAntiCheat={showAntiCheat} />
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-500">Žádná data pro toto kolo</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const RoundTaskInfo = ({ roundData }) => {
  if (!roundData?.type) return null;
  const items = [];

  if (roundData.question) {
    items.push(
      <div key="question" className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-1">Otázka</div>
        <div className="text-gray-800 font-medium">{roundData.question}</div>
        {roundData.type === 'abcd' && roundData.options?.length > 0 && (
          <div className="mt-3 space-y-1">
            {roundData.options.map((opt, i) => {
              const letter = ['A', 'B', 'C', 'D'][i];
              const isCorrect = letter.toLowerCase() === roundData.correctAnswer?.toLowerCase();
              return (
                <div key={i} className={`text-sm px-3 py-1.5 rounded-lg ${isCorrect ? 'bg-green-200 border border-green-400 font-bold text-green-800' : 'bg-white border border-gray-200 text-gray-700'}`}>
                  {letter}) {opt}{isCorrect ? ' ✓' : ''}
                </div>
              );
            })}
          </div>
        )}
        {roundData.correctAnswer && roundData.type !== 'abcd' && (
          <div className="mt-2 text-sm text-green-700 font-semibold">Správná odpověď: {roundData.correctAnswer}</div>
        )}
      </div>
    );
  }

  if (roundData.statement) {
    items.push(
      <div key="statement" className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-wide mb-1">Tvrzení</div>
        <div className="text-gray-800 font-medium">{roundData.statement}</div>
        <div className="mt-2 text-sm font-bold">
          Správná odpověď:{' '}
          <span className={roundData.correctAnswer === 'true' ? 'text-green-600' : 'text-red-600'}>
            {roundData.correctAnswer === 'true' ? '✅ Pravda' : '❌ Nepravda'}
          </span>
        </div>
      </div>
    );
  }

  if (roundData.sentenceTemplate) {
    const blanks = [];
    const regex = /\[\[(.*?)\]\]/g;
    let m;
    while ((m = regex.exec(roundData.sentenceTemplate)) !== null) blanks.push(m[1]);
    items.push(
      <div key="fill-sentence" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-1">Věta k doplnění</div>
        <div className="text-gray-800 font-mono text-sm leading-relaxed">{roundData.sentenceTemplate}</div>
        {blanks.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {blanks.map((b, i) => (
              <span key={i} className="bg-green-100 border border-green-300 rounded px-2 py-0.5 text-sm font-semibold text-green-800">{i + 1}. {b}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (roundData.sortItems?.filter(Boolean).length > 0) {
    items.push(
      <div key="sort" className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="text-xs font-bold text-orange-700 uppercase tracking-wide mb-2">Správné pořadí</div>
        <div className="space-y-1">
          {roundData.sortItems.filter(Boolean).map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="w-6 h-6 rounded-full bg-orange-400 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
              <span className="text-gray-800">{item}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (roundData.codeTemplate) {
    items.push(
      <div key="code-template">
        <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Šablona kódu</div>
        <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto whitespace-pre-wrap">{roundData.codeTemplate}</pre>
        {roundData.correctFill && (
          <div className="mt-1 text-sm text-green-700 font-semibold">
            Správné doplnění: <code className="bg-gray-100 px-2 py-0.5 rounded font-mono">{roundData.correctFill}</code>
          </div>
        )}
      </div>
    );
  }

  if (roundData.buggyCode) {
    items.push(
      <div key="buggy-code">
        <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Kód s chybou</div>
        <pre className="bg-gray-900 text-yellow-400 p-4 rounded-lg text-sm font-mono overflow-x-auto">
          {roundData.buggyCode.split('\n').map((line, i) => (
            <div key={i} className={i + 1 === roundData.bugLine ? 'bg-red-900 text-red-200' : ''}>
              <span className="text-gray-500 mr-3 select-none">{String(i + 1).padStart(2, ' ')}</span>{line}
            </div>
          ))}
        </pre>
        <div className="mt-1 text-sm text-red-600 font-semibold">
          🐛 Chyba na řádku {roundData.bugLine}{roundData.bugExplanation ? `: ${roundData.bugExplanation}` : ''}
        </div>
      </div>
    );
  }

  if (roundData.drawPrompt) {
    items.push(
      <div key="draw-prompt" className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="text-xs font-bold text-purple-600 uppercase tracking-wide mb-1">Zadání pro kreslení</div>
        <div className="text-gray-800 font-medium text-lg">{roundData.drawPrompt}</div>
      </div>
    );
  }

  if (roundData.taskDescription) {
    items.push(
      <div key="task-desc" className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Doplňující pokyn</div>
        <div className="text-gray-700 text-sm">{roundData.taskDescription}</div>
      </div>
    );
  }

  if (items.length === 0) return null;
  return <div className="space-y-3">{items}</div>;
};

const renderAnswer = (answer, roundData) => {
  if (!answer) return '—';
  if (answer.imageData) return '🎨 Kresba';
  if (answer.selectedLine !== undefined) return `Řádek ${answer.selectedLine}`;
  if (answer.order?.length) return answer.order.join(' → ');
  if (answer.blanks?.length) return answer.blanks.join(', ');
  if (answer.answer !== undefined) {
    if (roundData?.type === 'true-false') return answer.answer === 'true' ? '✅ Pravda' : '❌ Nepravda';
    return String(answer.answer);
  }
  return '—';
};

const PointsBadge = ({ points, onClick }) => {
  const color = points > 0
    ? 'text-green-700 border-green-300 bg-green-50 hover:border-green-500'
    : points < 0
    ? 'text-red-700 border-red-300 bg-red-50 hover:border-red-500'
    : 'text-gray-500 border-gray-200 bg-white hover:border-gray-400';
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border-2 font-mono font-bold text-sm transition-colors ${color}`}
      title="Kliknout pro úpravu bodů"
    >
      {points > 0 ? '+' : ''}{points}
      <span className="text-xs opacity-60 ml-0.5">✏️</span>
    </button>
  );
};

const SoloRoundReview = ({ round, roundIndex, onGradeSolo, saving, showAntiCheat }) => {
  const roundData = round.round || {};
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (sa) => { setEditingId(sa.sessionId); setEditValue(String(sa.points)); };
  const cancelEdit = () => { setEditingId(null); setEditValue(''); };

  const saveEdit = async (sessionId) => {
    const pts = parseFloat(editValue);
    if (isNaN(pts)) return;
    await onGradeSolo(roundIndex, sessionId, pts);
    setEditingId(null);
    setEditValue('');
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h2 className="text-2xl font-bold text-gray-800">{GAME_MODE_LABELS[roundData.type] || roundData.type}</h2>
          <span className="text-xs bg-blue-100 text-blue-700 font-semibold px-2 py-0.5 rounded-full">Solo kolo</span>
        </div>
        <p className="text-xs text-gray-400 mb-4">Automatické vyhodnocení — klikni na body u žáka pro ruční úpravu</p>
        <RoundTaskInfo roundData={roundData} />
      </div>

      <div>
        <h3 className="font-bold text-gray-700 mb-3">Odpovědi žáků ({round.soloAnswers?.length || 0})</h3>
        <div className="space-y-2">
          {(round.soloAnswers || []).map((sa, i) => {
            const elapsed = sa.answer?.submittedAt && round.roundStartedAt
              ? Math.round((sa.answer.submittedAt - round.roundStartedAt) / 1000)
              : null;
            const isEditing = editingId === sa.sessionId;
            const answerText = renderAnswer(sa.answer, roundData);
            const hasImage = !!sa.answer?.imageData;

            return (
              <div
                key={i}
                className={`rounded-xl border-2 overflow-hidden transition-colors ${
                  sa.correct === true ? 'border-green-300 bg-green-50'
                  : sa.correct === false ? 'border-red-300 bg-red-50'
                  : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xl flex-shrink-0">
                      {sa.correct === true ? '✅' : sa.correct === false ? '❌' : sa.answer ? '🟡' : '⏰'}
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-800">{sa.name}</div>
                      {elapsed !== null && <div className="text-xs text-gray-400">odpověděl za {elapsed}s</div>}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    {!hasImage && (
                      <span className="font-mono text-sm bg-white border border-gray-200 rounded px-2 py-1 text-gray-700 max-w-xs truncate">
                        {answerText}
                      </span>
                    )}
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          step="0.5"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveEdit(sa.sessionId); if (e.key === 'Escape') cancelEdit(); }}
                          className="w-20 px-2 py-1.5 border-2 border-primary-400 rounded-lg text-center font-mono font-bold focus:outline-none text-sm"
                          autoFocus
                        />
                        <button onClick={() => saveEdit(sa.sessionId)} disabled={saving} className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50">✓</button>
                        <button onClick={cancelEdit} className="px-2 py-1.5 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg text-sm">✕</button>
                      </div>
                    ) : (
                      <PointsBadge points={sa.points} onClick={() => startEdit(sa)} />
                    )}
                  </div>
                </div>

                {hasImage && (
                  <div className="px-4 pb-4">
                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white max-w-sm">
                      <img src={sa.answer.imageData} alt={`Kresba — ${sa.name}`} className="w-full" />
                    </div>
                  </div>
                )}

                {showAntiCheat && (
                  <div className="px-4 pb-4">
                    <AntiCheatReport antiCheatData={sa.antiCheatData} studentName={sa.name} />
                  </div>
                )}
              </div>
            );
          })}

          {(round.soloAnswers || []).length === 0 && (
            <div className="text-center py-8 text-gray-400 italic">Žádné odpovědi v tomto kole</div>
          )}
        </div>
      </div>
    </div>
  );
};

const InteractiveRoundReview = ({ round, roundIndex, onGrade, saving, showAntiCheat }) => {
  const roundData = round.round || {};
  const [editingPairs, setEditingPairs] = useState(new Set());
  const [customInputs, setCustomInputs] = useState({});

  const openEdit = (pairIndex) => setEditingPairs(prev => { const s = new Set(prev); s.add(pairIndex); return s; });
  const closeEdit = (pairIndex) => setEditingPairs(prev => { const s = new Set(prev); s.delete(pairIndex); return s; });

  const handlePreset = async (pairIndex, type, bonus) => {
    await onGrade(roundIndex, pairIndex, type, bonus);
    closeEdit(pairIndex);
  };

  const setCustom = (pairIndex, field, value) => {
    setCustomInputs(prev => ({ ...prev, [pairIndex]: { ...(prev[pairIndex] || {}), [field]: value } }));
  };

  const handleCustomSubmit = async (pairIndex) => {
    const inp = customInputs[pairIndex] || {};
    const creator = parseFloat(inp.creator ?? '0');
    const solver = parseFloat(inp.solver ?? '0');
    if (isNaN(creator) || isNaN(solver)) return;
    await onGrade(roundIndex, pairIndex, 'custom', false, { creator, solver });
    closeEdit(pairIndex);
    setCustomInputs(prev => { const n = { ...prev }; delete n[pairIndex]; return n; });
  };

  const pts = (val) => val > 0 ? `+${val}` : String(val);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-5">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-2xl font-bold text-gray-800">{GAME_MODE_LABELS[roundData.type] || roundData.type}</h2>
          <span className="text-xs bg-purple-100 text-purple-700 font-semibold px-2 py-0.5 rounded-full">Interaktivní kolo</span>
        </div>
        <RoundTaskInfo roundData={roundData} />
      </div>

      {(round.pairs || []).map((pair, pairIndex) => {
        const grading = pair.grading;
        const isGraded = grading?.type;
        const isEditing = editingPairs.has(pairIndex);
        const inp = customInputs[pairIndex] || {};

        const gradedLabel = grading?.type === 'custom'
          ? '✏️ Vlastní body'
          : GRADE_OPTIONS.find(o => o.type === grading?.type && o.bonus === (grading?.bonus || false))?.label || grading?.type;

        return (
          <div
            key={pairIndex}
            className={`bg-white rounded-xl shadow-lg p-6 border-2 transition-colors ${isGraded && !isEditing ? 'border-green-300' : 'border-gray-200'}`}
          >
            <div className="grid grid-cols-2 gap-6 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-lg">✍️</span>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Tvůrce</div>
                  <div className="font-bold text-lg text-gray-800">{pair.creatorName}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">🧩</span>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Řešitel</div>
                  <div className="font-bold text-lg text-gray-800">{pair.solverName}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-sm font-semibold text-gray-600 mb-2">Zadání tvůrce:</div>
                {pair.submission?.code && pair.submission.code !== '// Neodevzdáno' ? (
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">{pair.submission.code}</pre>
                ) : null}
                {pair.submission?.description ? (
                  <div className="bg-gray-100 p-3 rounded-lg text-sm mt-2">{pair.submission.description}</div>
                ) : null}
                {(!pair.submission?.code || pair.submission.code === '// Neodevzdáno') && !pair.submission?.description && (
                  <div className="bg-gray-100 p-3 rounded-lg text-sm text-gray-400 italic">Neodevzdáno</div>
                )}
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-600 mb-2">Odpověď řešitele:</div>
                {pair.solution?.imageData ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <img src={pair.solution.imageData} alt="Kresba řešitele" className="w-full" />
                  </div>
                ) : pair.solution?.code ? (
                  <pre className="bg-gray-900 text-blue-400 p-4 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">{pair.solution.code}</pre>
                ) : pair.solution?.text ? (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm">{pair.solution.text}</div>
                ) : (
                  <div className="bg-gray-100 p-3 rounded-lg text-sm text-gray-400 italic">Neodevzdáno</div>
                )}
              </div>
            </div>

            {showAntiCheat && (
              <div className="mb-4">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">🛡️ Anti-cheat — {pair.solverName} (řešitel)</div>
                <AntiCheatReport antiCheatData={pair.antiCheatData} studentName={pair.solverName} />
              </div>
            )}

            {isGraded && !isEditing ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-300 rounded-xl px-4 py-3">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-green-700 font-semibold text-sm">{gradedLabel}</span>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-600">
                      {pair.creatorName}:{' '}
                      <strong className={grading.creatorPoints > 0 ? 'text-green-600' : grading.creatorPoints < 0 ? 'text-red-600' : 'text-gray-500'}>
                        {pts(grading.creatorPoints)}
                      </strong>
                    </span>
                    <span className="text-gray-400">|</span>
                    <span className="text-gray-600">
                      {pair.solverName}:{' '}
                      <strong className={grading.solverPoints > 0 ? 'text-green-600' : grading.solverPoints < 0 ? 'text-red-600' : 'text-gray-500'}>
                        {pts(grading.solverPoints)}
                      </strong>
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => openEdit(pairIndex)}
                  disabled={saving}
                  className="text-sm text-primary-600 hover:text-primary-800 font-semibold underline disabled:opacity-50 flex-shrink-0"
                >
                  Změnit hodnocení
                </button>
              </div>
            ) : (
              <div className="space-y-3 border-t border-gray-100 pt-4">
                <div className="flex flex-wrap gap-2">
                  {GRADE_OPTIONS.map(opt => (
                    <button
                      key={`${opt.type}-${opt.bonus}`}
                      onClick={() => handlePreset(pairIndex, opt.type, opt.bonus)}
                      disabled={saving}
                      className={`flex-1 min-w-[130px] ${opt.color} text-white px-3 py-2 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50`}
                    >
                      {opt.icon} {opt.label} ({opt.detail})
                    </button>
                  ))}
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Vlastní počet bodů</div>
                  <div className="flex items-end gap-4 flex-wrap">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{pair.creatorName} (tvůrce)</label>
                      <input
                        type="number"
                        step="0.5"
                        value={inp.creator ?? ''}
                        onChange={e => setCustom(pairIndex, 'creator', e.target.value)}
                        placeholder="0"
                        className="w-24 px-2 py-2 border-2 border-gray-300 rounded-lg text-center font-mono text-sm focus:border-primary-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{pair.solverName} (řešitel)</label>
                      <input
                        type="number"
                        step="0.5"
                        value={inp.solver ?? ''}
                        onChange={e => setCustom(pairIndex, 'solver', e.target.value)}
                        placeholder="0"
                        className="w-24 px-2 py-2 border-2 border-gray-300 rounded-lg text-center font-mono text-sm focus:border-primary-400 focus:outline-none"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCustomSubmit(pairIndex)}
                        disabled={saving || (inp.creator === undefined && inp.solver === undefined)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        Uložit vlastní
                      </button>
                      {isEditing && (
                        <button onClick={() => closeEdit(pairIndex)} className="px-3 py-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg text-sm">
                          Zrušit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {(!round.pairs || round.pairs.length === 0) && (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-500">Žádné páry k hodnocení v tomto kole</div>
      )}
    </div>
  );
};

export default ReviewGame;
